import { RefCarePlanProblemResponse } from '~/lib/types/carePlan/RefCarePlanProblemResponse';
import { RefCarePlanManifestationResponse } from '~/lib/types/carePlan/RefCarePlanManifestationResponse';
import {
  ChakraBox,
  ChakraFlex,
  MaudCardFrame,
  MultiSelectOption,
  Text,
  TextInputV2,
  UniversalSelect,
} from '@equip.health/ui';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { AddEditManifestationRow } from '~/components/maud/patients/profile/tabs/careplan/AddEditManifestationRow';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ManifestationCodeToEditableDataMap } from '~/lib/types/carePlan/ManifestationCodeToEditableDataMap';
import {
  getNotYetAssessedCode,
  useCarePlanFrequencyOptions,
} from '~/lib/hooks/useCarePlanFrequencyOptions';

type Props = {
  manifestationsForED: RefCarePlanManifestationResponse[];
  modalId: string;
  problem: RefCarePlanProblemResponse;
  problemDescription: string;
  problemFrequencyCode?: string;
  selectedManifestationsMap: ManifestationCodeToEditableDataMap;
  setProblemDescription: Dispatch<SetStateAction<string>>;
  setProblemFrequencyCode: Dispatch<SetStateAction<string | undefined>>;
  setSelectedManifestationsMap: Dispatch<
    SetStateAction<ManifestationCodeToEditableDataMap>
  >;
};
export const AddProblemModalManifestationsStep = ({
  manifestationsForED,
  modalId,
  problem,
  problemDescription,
  problemFrequencyCode,
  selectedManifestationsMap,
  setProblemDescription,
  setProblemFrequencyCode,
  setSelectedManifestationsMap,
}: Props) => {
  const {
    carePlanProviderFacingLanguage: isProviderFacingLanguageEnabled,
    manifestationProblemStatusFrequency: isFrequencyDisplayEnabled,
  } = useFlags<LDFlags>();

  const { options: frequencyOptions, placeholder: frequencyPlaceholder } =
    useCarePlanFrequencyOptions({
      frequencyUnitType: problem.frequencyUnitType,
      frequencyCode: problemFrequencyCode,
    });

  useEffect(() => {
    if (problem.frequencyUnitType && problemFrequencyCode === undefined) {
      setProblemFrequencyCode(getNotYetAssessedCode(problem.frequencyUnitType));
    }
  }, [
    problem.frequencyUnitType,
    problemFrequencyCode,
    setProblemFrequencyCode,
  ]);

  return (
    <MaudCardFrame id="add-problem-modal-manifestations-step">
      <Text marginBottom="18px" variant="h5">
        {problem.label}
      </Text>
      <ChakraFlex flexDirection="column" gap="24px">
        {manifestationsForED.length === 0 && (
          <TextInputV2
            id="add-problem-description"
            onChange={(newDescription) => {
              setProblemDescription(newDescription);
            }}
            placeholder="Please add any additional context"
            value={problemDescription}
          />
        )}
        {(isProviderFacingLanguageEnabled || isFrequencyDisplayEnabled) && (
          <ChakraFlex alignItems="center" gap="12px">
            {isProviderFacingLanguageEnabled &&
              problem.providerFacingLanguage && (
                <Text>{problem.providerFacingLanguage}</Text>
              )}
            {isFrequencyDisplayEnabled && problem.frequencyUnitType && (
              <ChakraBox width="250px">
                <UniversalSelect
                  ariaLabel="Select frequency"
                  id="add-problem-select-manifestation-frequency"
                  isClearable={false}
                  isSingleSelect
                  menuPlacement="auto"
                  modalId={modalId}
                  onChange={(newFrequency: MultiSelectOption) => {
                    setProblemFrequencyCode(newFrequency.value);
                  }}
                  options={frequencyOptions}
                  placeholder={frequencyPlaceholder}
                  value={frequencyOptions.find(
                    (option) => option.value === problemFrequencyCode,
                  )}
                  variant="small"
                />
              </ChakraBox>
            )}
          </ChakraFlex>
        )}

        {manifestationsForED.map((manifestation) => (
          <AddEditManifestationRow
            key={manifestation.code}
            manifestation={manifestation}
            modalId={modalId}
            selectedManifestationsMap={selectedManifestationsMap}
            setSelectedManifestationsMap={setSelectedManifestationsMap}
          />
        ))}
      </ChakraFlex>
    </MaudCardFrame>
  );
};
